import { groupBy } from 'lodash'
import { Navigation } from 'navigation'
import { ApfFieldsResponse } from 'orders/ordersService'
import {
    ProductsMetaData,
    Article,
    FilterOption,
    FilterCategory,
    FilterQueryItems,
    Filters,
} from 'uiComponents/filter/schema'

export const getProductOptions = (products: ProductsMetaData[]): FilterOption[] => {
    const onlyArticles = products.filter((p) => !p.children && !p.articles).length === products.length
    return onlyArticles ? getMappedArticles(products as any as Article[], []) : getMappedProductLists(products, [])
}

export const getProductTreeOptions = (products: Article[], categories: ProductsMetaData[]) => {
    const options = !!categories.length ? getMappedProductLists(categories, []) : getMappedArticles(products, [])
    return options
}

const getMappedProductLists = (productList: ProductsMetaData[], parents: string[]): FilterOption[] => {
    return productList.map((pl) => ({
        name: pl.name,
        slug: pl.uuid,
        category: 'products' as FilterCategory,
        parents: parents,
        children:
            pl.children && pl.children.length > 0
                ? getMappedProductLists(pl.children, [...parents, pl.uuid])
                : getMappedArticles(pl.articles, [...parents, pl.uuid]),
        leafNode: !pl.children && !pl.articles,
    }))
}

const getMappedArticles = (articles: Article[], parents: string[]): FilterOption[] => {
    return articles.map((a) => ({
        name: a.name,
        slug: a.id,
        category: 'products' as FilterCategory,
        parents,
        children: [],
        leafNode: true,
    }))
}

interface OptionConfigItem {
    name: string
    slug: string
    divider?: boolean
    heading?: string
}

export const formatFlatOptions = (category: FilterCategory, optionsConfig: OptionConfigItem[]) => {
    return optionsConfig.map((o) => ({
        category,
        name: o.name,
        slug: o.slug,
        parents: [],
        children: [],
        leafNode: true,
        divider: o.divider,
        heading: o.heading,
    }))
}

export const renderFilterQuery = (filters: FilterOption[] | FilterQueryItems[]) => {
    const groupedFilters = groupBy(filters, (x) => x.category)
    const filterString = Object.entries(groupedFilters)
        .map(([k, v]) => `${k}:${v.map((x) => x.slug).join(',')}`)
        .join(';')
    return filterString
}

export const parseFilterQuery = (filterString: string): Filters[] => {
    return !filterString
        ? []
        : decodeURIComponent(filterString)
              .split(';')
              .map((f) => f.split(':'))
              .map((f) => ({ attribute: f[0], values: f[1].split(',') }))
}

export const parseFilterQueryIntoOptions = (filterString: string): FilterQueryItems[] => {
    const result: FilterQueryItems[] = []
    const categoriesList = !filterString
        ? []
        : decodeURIComponent(filterString)
              .split(';')
              .map((f) => f.split(':'))
              .map((f) => ({ category: f[0], values: f[1].split(',') }))
    categoriesList.forEach((item) => {
        item.values.forEach((v) => {
            result.push({
                category: item.category as FilterCategory,
                slug: v,
            })
        })
    })
    return result
}

export const getFiltersFromUrl = (filterCategory: FilterCategory): string => {
    const filterParam = new URLSearchParams(location.search).get('filter')
    if (!filterParam) return ''

    const filter = filterParam.split(';').find((uriComponent) => uriComponent.startsWith(`${filterCategory}:`))

    if (!filter) return ''

    const [, value] = filter.split(':')
    return value
}

export const setFilterValue = (filterCategory: FilterCategory, value: string, navigation: Navigation) => {
    const currentFilter = new URLSearchParams(location.search).get('filter') || ''
    const currentFilterWithoutCategory = currentFilter.replace(new RegExp(`;?${filterCategory}:[^;]*`), '')
    const newFilter = `${currentFilterWithoutCategory};${filterCategory}:${value}`.replace(/^;/, '')

    navigation.addQueryWithReplace({
        filter: newFilter,
    })
}

export const getApfFieldsOptions = (apfFields: ApfFieldsResponse) => {
    let fields = []
    const customFields = Object.entries(apfFields.custom).map(([key, value]) => ({ name: value, slug: key }))
    const defaultFields = Object.entries(apfFields.default).map(([key, value]) => ({ name: value, slug: key }))
    const emailField = { name: 'Email', slug: 'buyerEmail' }
    const divider = { name: '', slug: '', divider: true }
    const heading = { name: '', slug: '', heading: 'Custom fields' }

    if (customFields.length > 0) {
        fields = [...defaultFields, emailField, divider, heading, ...customFields]
    } else {
        fields = [...defaultFields, emailField]
    }

    return formatFlatOptions('apf_fields', fields)
}
